// src/lib/api/admin.ts — all /v1/admin/* endpoints (PRI-291 Phase 7)
// settings, waitlist, emails, leads, analytics, investors, demo-videos, invites, etc.

// PRI-496 P0: /v1/admin/* is internal — route through the internal-api-client
// chokepoint (internalRequest === legacy request(); zero behavior change).
import { internalRequest as request } from '../../core/lib/internal-api-client'

// ── Admin Settings ────────────────────────────────────────────────────────────

export interface AdminSettings {
  max_upload_bytes: number
  free_max_duration_seconds: number
  pro_max_duration_seconds: number
  demo_analyses_per_day: number
  output_masks?: number
  video_sample_fps?: number
  frames_per_segment?: number
  process_resolution?: number
  mask_confidence_threshold?: number
  narrative_enabled?: number
  narrative_threshold?: number
  narrative_stride_frames?: number
}

export function getAdminSettings(): Promise<AdminSettings> {
  return request('/v1/admin/settings')
}

export function patchAdminSettings(patch: Partial<AdminSettings>): Promise<{ ok: boolean; applied: string[]; warning?: string }> {
  return request('/v1/admin/settings', {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })
}

export function reloadPrompts(): Promise<{ ok: boolean; reloaded_at?: string }> {
  return request('/v1/admin/prompts/reload', { method: 'POST' })
}

export function updatePrompts(prompts: string[]): Promise<{ ok: boolean; count: number; prompts: string[] }> {
  return request('/v1/admin/prompts', {
    method: 'PUT',
    body: JSON.stringify({ prompts }),
  })
}

// ── Admin Waitlist ────────────────────────────────────────────────────────────

export interface WaitlistEntry {
  id: string
  email: string
  full_name: string
  company: string
  use_case: string
  status: string
  score: number | null
  created_at: string
  invited_at: string | null
  /** True once the person created an account AND ran a real (non-demo) prompt to completion. */
  activated?: boolean
  /** ISO timestamp of the first completed real prompt, or null if not activated. */
  activated_at?: string | null
  /**
   * PRI-387: true iff a `users` row exists for this waitlist email. An
   * `accepted` entry with account_created:false is a dead-end invite (the
   * person was accepted but never created an account), which otherwise looks
   * identical to a real success in the admin view.
   */
  account_created?: boolean
}

export function getAdminWaitlist(params?: {
  status?: string
  limit?: number
  offset?: number
}): Promise<{ entries: WaitlistEntry[]; total: number }> {
  const qs = new URLSearchParams()
  if (params?.status) qs.set('status', params.status)
  if (params?.limit != null) qs.set('limit', String(params.limit))
  if (params?.offset != null) qs.set('offset', String(params.offset))
  const query = qs.toString() ? `?${qs.toString()}` : ''
  return request(`/v1/admin/waitlist${query}`)
}

export function adminInvite(id: string): Promise<{ id: string; email: string; invite_token: string; invited_at: string }> {
  return request(`/v1/admin/waitlist/${id}/invite`, { method: 'POST' })
}

export interface AdminDeleteWaitlistResult {
  ok: boolean
  id?: string
  email: string
  scrubbed: {
    user_ids: string[]
    clerk_user_ids: string[]
    s3_objects_deleted: number
  }
}

export function adminDeleteWaitlistEntry(id: string): Promise<AdminDeleteWaitlistResult> {
  return request(`/v1/admin/waitlist/${id}`, { method: 'DELETE' })
}

// ── Admin Email Queue ─────────────────────────────────────────────────────────

export interface EmailQueueItem {
  id: string
  lead_id: string | null
  to_email: string
  to_name: string | null
  from_email: string
  reply_to: string | null
  subject: string
  body_html: string
  body_text: string | null
  template_type: string | null
  metadata: Record<string, unknown>
  status: 'pending' | 'approved' | 'rejected' | 'sent' | 'failed'
  queued_at: string
  reviewed_at: string | null
  reviewed_by: string | null
  sent_at: string | null
  rejection_reason: string | null
  resend_message_id: string | null
}

export interface EmailDraft {
  lead_id: string
  to_email: string
  to_name: string | null
  from_email: string
  reply_to: string | null
  template_type: string
  subject: string
  body_text: string
  body_html: string
}

export function getAdminEmails(status?: string): Promise<{ emails: EmailQueueItem[]; total: number }> {
  const qs = status && status !== 'all' ? `?status=${encodeURIComponent(status)}` : ''
  return request(`/v1/admin/emails${qs}`)
}

export function getAdminEmail(id: string): Promise<EmailQueueItem> {
  return request(`/v1/admin/emails/${id}`)
}

export function approveEmail(id: string): Promise<EmailQueueItem> {
  return request(`/v1/admin/emails/${id}/approve`, { method: 'POST' })
}

export function rejectEmail(id: string, reason?: string): Promise<EmailQueueItem> {
  return request(`/v1/admin/emails/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  })
}

export function editEmail(id: string, fields: Partial<Pick<EmailQueueItem, 'subject' | 'body_html' | 'body_text' | 'to_name' | 'reply_to' | 'from_email'>>): Promise<EmailQueueItem> {
  return request(`/v1/admin/emails/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(fields),
  })
}

export function queueEmail(payload: {
  lead_id?: string
  to_email: string
  to_name?: string
  from_email?: string
  reply_to?: string
  subject: string
  body_html: string
  body_text?: string
  template_type?: string
  metadata?: Record<string, unknown>
}): Promise<EmailQueueItem> {
  return request('/v1/admin/emails/queue', { method: 'POST', body: JSON.stringify(payload) })
}

export function draftEmail(payload: {
  lead_id: string
  template_type?: string
  custom_instructions?: string
}): Promise<EmailDraft> {
  return request('/v1/admin/emails/draft', { method: 'POST', body: JSON.stringify(payload) })
}

// ── Admin Leads / CRM ────────────────────────────────────────────────────────

export interface Lead {
  id: string
  email: string
  full_name: string | null
  company: string | null
  score: number | null
  status: string | null
  source: string | null
  vertical: string | undefined
  icp_total: number | undefined
  what_they_do: string | undefined
  contact_title: string | undefined
  introduced_by: string | null | undefined
  website: string | undefined
  next_action: string | undefined
  last_interaction?: { channel: string; occurred_at: string; outcome: string | null } | null
  win_loss_signal?: string | null
  is_primary?: boolean
  created_at: string
}

export interface IcpScore {
  pa?: number
  tf?: number
  ba?: number
  tl?: number
  bs?: number
  total?: number
}

export interface ClayEnrichment {
  vertical?: string
  website?: string
  what_they_do?: string
  next_action?: string
  introduced_by?: string | null
  contact_title?: string
  linkedin_url?: string
  icp_score?: IcpScore
}

export interface LeadDetail {
  id: string
  email: string
  full_name: string | null
  company: string | null
  score: number | null
  status: string | null
  source: string | null
  clay_enrichment: ClayEnrichment | null
  user_id: string | null
  created_at: string
  updated_at: string
  interactions: Interaction[]
}

export interface Interaction {
  id: string
  lead_id: string
  direction: string
  channel: string
  notes: string | null
  outcome: string | null
  metadata: Record<string, unknown>
  created_by: string
  occurred_at: string
  created_at: string
}

export interface NewInteraction {
  channel: 'call' | 'email' | 'linkedin' | 'meeting' | 'demo' | 'note' | 'discovery_call' | 'other'
  direction?: 'inbound' | 'outbound'
  notes?: string
  outcome?: 'interested' | 'not_interested' | 'follow_up' | 'no_response' | 'converted' | 'unsubscribed'
  occurred_at?: string
  metadata?: Record<string, unknown>
}

export interface WinLossSummary {
  total: number
  counts: { win: number; loss: number; pending: number; nurture: number; unset: number }
  recent: Array<{
    id: string
    lead_id: string
    notes: string | null
    metadata: Record<string, unknown>
    occurred_at: string
    company: string | null
    full_name: string | null
  }>
}

export interface NewLead {
  company: string
  full_name?: string
  email?: string
  contact_title?: string
  website?: string
  vertical?: 'Smart Camera' | 'AV' | 'Drones' | 'Robots' | 'Defense' | 'Video Ad Tech' | 'Other'
  introduced_by?: string
  next_action?: string
  notes?: string
  source?: string
  status?: 'prospect' | 'engaged' | 'disqualified'
}

export function createLead(data: NewLead): Promise<Lead> {
  return request<Lead>('/v1/admin/leads', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export function getAdminLeads(params?: {
  status?: string
  vertical?: string
  sort?: string
  q?: string
}): Promise<Lead[]> {
  const qs = new URLSearchParams()
  if (params?.status) qs.set('status', params.status)
  if (params?.vertical) qs.set('vertical', params.vertical)
  if (params?.sort) qs.set('sort', params.sort)
  if (params?.q) qs.set('q', params.q)
  const query = qs.toString() ? `?${qs.toString()}` : ''
  return request<Lead[]>(`/v1/admin/leads${query}`)
}

export function makeLeadPrimary(id: string): Promise<Lead> {
  return request<Lead>(`/v1/admin/leads/${id}/make-primary`, { method: 'POST' })
}

export function getAdminLead(id: string): Promise<LeadDetail> {
  return request<LeadDetail>(`/v1/admin/leads/${id}`)
}

export function updateLead(id: string, fields: Partial<Record<string, unknown>>): Promise<LeadDetail> {
  return request<LeadDetail>(`/v1/admin/leads/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(fields),
  })
}

export function getInteractions(leadId: string): Promise<Interaction[]> {
  return request<Interaction[]>(`/v1/admin/leads/${leadId}/interactions`)
}

export function logInteraction(leadId: string, data: NewInteraction): Promise<Interaction> {
  return request<Interaction>(`/v1/admin/leads/${leadId}/interactions`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export function deleteInteraction(interactionId: string): Promise<void> {
  return request<void>(`/v1/admin/interactions/${interactionId}`, { method: 'DELETE' })
}

export function getWinLossSummary(): Promise<WinLossSummary> {
  return request<WinLossSummary>('/v1/admin/winloss')
}

// ── Growth Analytics ──────────────────────────────────────────────────────────

export interface GrowthAnalytics {
  range: '12w' | '6m' | 'all'
  northStar: {
    weekly: Array<{ week: string; minutes: number }>
    cumulative: Array<{ week: string; minutes: number }>
    currentWeek: number
    previousWeek: number
    wowGrowth: number | null
  }
  acquisition: {
    weeklySignups: Array<{ week: string; signups: number }>
    cumulativeUsers: Array<{ week: string; total: number }>
    totalUsers: number
    activatedUsers: number
    activationRate: number
    thisWeekSignups: number
    waitlistFunnel: {
      total: number
      invited: number
      signedUp: number
      inviteConversionRate: number
      signupConversionRate: number
    }
    w1Retention: number | null
  }
  revenue: {
    currentMrr: number
    currentArr: number
    weeklyMrr: Array<{ week: string; mrr: number }>
    wowMrrGrowth: number | null
    payingUsers: number
    totalUsers: number
    freeToProRate: number
  }
}

export function getGrowthAnalytics(range: '12w' | '6m' | 'all' = '12w'): Promise<GrowthAnalytics> {
  return request<GrowthAnalytics>(`/v1/admin/analytics/growth?range=${range}`)
}

// ── Admin Investors ───────────────────────────────────────────────────────────

export type InvestorStage =
  | 'researching'
  | 'reached_out'
  | 'meeting_booked'
  | 'pitched'
  | 'diligence'
  | 'passed'
  | 'committed'

export interface Investor {
  id: string
  created_at: string
  updated_at: string
  name: string
  firm: string
  fund_stage: string | null
  check_size: string | null
  contact_path: string | null
  stage: InvestorStage
  notes: string | null
  next_action: string | null
  next_action_due: string | null
  last_contacted: string | null
  email: string | null
  linkedin: string | null
  last_brief: InvestorBrief | null
  amount_invested: number | null
  is_current_investor: boolean
  receives_updates: boolean
  contacts: InvestorContact[]
}

export interface InvestorDetail extends Investor {
  interactions: InvestorInteraction[]
}

export interface InvestorInteraction {
  id: string
  investor_id: string
  lead_id: string | null
  direction: string
  channel: string
  notes: string | null
  outcome: string | null
  metadata: Record<string, unknown>
  created_by: string
  occurred_at: string
  created_at: string
}

export interface InvestorBrief {
  background?: string
  company?: string
  connection_to_primate?: string
  suggested_agenda?: string[]
  questions_to_ask?: string[]
  sensitivities?: string[]
  key_points_about_primate?: string[]
  [key: string]: unknown
}

export interface InvestorContact {
  id: string
  investor_id: string
  name: string
  email: string | null
  linkedin: string | null
  is_primary: boolean
  receives_updates: boolean
  created_at: string
  updated_at: string
}

export function getInvestors(stage?: string): Promise<Investor[]> {
  const qs = stage ? `?stage=${encodeURIComponent(stage)}` : ''
  return request<Investor[]>(`/v1/admin/investors${qs}`)
}

export function getInvestor(id: string): Promise<InvestorDetail> {
  return request<InvestorDetail>(`/v1/admin/investors/${id}`)
}

export function createInvestor(data: Partial<Investor>): Promise<Investor> {
  return request<Investor>('/v1/admin/investors', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export function updateInvestor(id: string, fields: Partial<Investor>): Promise<Investor> {
  return request<Investor>(`/v1/admin/investors/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(fields),
  })
}

export function deleteInvestor(id: string): Promise<void> {
  return request<void>(`/v1/admin/investors/${id}`, { method: 'DELETE' })
}

export function createContact(
  investorId: string,
  data: { name: string; email?: string | null; linkedin?: string | null; receives_updates?: boolean },
): Promise<InvestorContact> {
  return request<InvestorContact>(`/v1/admin/investors/${investorId}/contacts`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export function updateContact(
  investorId: string,
  contactId: string,
  data: Partial<{ name: string; email: string | null; linkedin: string | null; receives_updates: boolean }>,
): Promise<InvestorContact> {
  return request<InvestorContact>(`/v1/admin/investors/${investorId}/contacts/${contactId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export function deleteContact(investorId: string, contactId: string): Promise<void> {
  return request<void>(`/v1/admin/investors/${investorId}/contacts/${contactId}`, { method: 'DELETE' })
}

export function makeContactPrimary(investorId: string, contactId: string): Promise<InvestorContact> {
  return request<InvestorContact>(`/v1/admin/investors/${investorId}/contacts/${contactId}/make-primary`, { method: 'POST' })
}

export function logInvestorInteraction(
  investorId: string,
  data: { direction?: string; channel: string; notes?: string; outcome?: string; occurred_at?: string },
): Promise<InvestorInteraction> {
  return request<InvestorInteraction>(`/v1/admin/investors/${investorId}/interactions`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export function prepInvestor(investorId: string, meetingContext?: string): Promise<InvestorBrief> {
  return request<InvestorBrief>(`/v1/admin/investors/${investorId}/prep`, {
    method: 'POST',
    body: JSON.stringify({ meeting_context: meetingContext }),
  })
}

export function composeInvestorEmail(
  investorId: string,
  templateType: string,
  tone?: string,
): Promise<{ id: string; investor_id: string; template: string; subject: string; status: string }> {
  return request(`/v1/admin/investors/${investorId}/compose`, {
    method: 'POST',
    body: JSON.stringify({ template_type: templateType, tone }),
  })
}

export interface MailingListResponse {
  investors: Array<{
    id: string
    name: string
    firm: string
    email: string | null
    is_current_investor: boolean
    amount_invested: number | null
  }>
  bcc_list: string
  total: number
}

export function getMailingList(): Promise<MailingListResponse> {
  return request<MailingListResponse>('/v1/admin/investors/mailing-list')
}

export function sendInvestorUpdate(subject: string, body: string): Promise<unknown> {
  return request('/v1/admin/investors/update-email', {
    method: 'POST',
    body: JSON.stringify({ subject, body }),
  })
}

// ── Cal.com ───────────────────────────────────────────────────────────────────

export function triggerCalcomEngagedLead(leadId: string): Promise<{ sent: boolean; to: string }> {
  return request(`/v1/admin/calcom/trigger-engaged-lead/${leadId}`, { method: 'POST' })
}

export function triggerCalcomInvestorFollowup(investorId: string): Promise<{ sent: boolean; to: string }> {
  return request(`/v1/admin/calcom/trigger-investor-followup/${investorId}`, { method: 'POST' })
}

// ── Admin Analytics: Demo Videos ─────────────────────────────────────────────

export interface DemoVideoStat {
  id: string;
  name: string;
  count: number;
  pct: number;
}

export function getDemoVideoAnalytics(range?: string): Promise<{ videos: DemoVideoStat[]; total: number }> {
  return request(`/v1/admin/analytics/demo-videos${range ? `?range=${range}` : ''}`)
}

// ── Admin Demo Video Library ──────────────────────────────────────────────────

export interface AdminDemoVideo {
  id: string;
  display_name: string;
  filename: string;
  thumbnail_url: string | null;
  file_size_bytes: number | null;
  duration_seconds: number | null;
  sort_order: number;
  results_by_prompt: Record<string, { answer: string | null; confidence: number | null; query_type: string | null; result_json: Record<string, unknown> | null; video_url: string | null; computed_at: string }>;
  status: 'uploading' | 'scanning' | 'ready' | 'error';
  error_message: string | null;
  scan_id: string | null;
  demo_video_results?: { prompt: string; answer: string | null; computed_at: string }[];
}

export function getAdminDemoVideos(): Promise<{ videos: AdminDemoVideo[] }> {
  return request('/v1/admin/demo-videos');
}

export function uploadDemoVideo(formData: FormData): Promise<{ ok: boolean; id: string; status: string }> {
  return request('/v1/admin/demo-videos', { method: 'POST', body: formData, skipContentType: true });
}

export function renameDemoVideo(id: string, display_name: string): Promise<{ ok: boolean; video: AdminDemoVideo }> {
  return request(`/v1/admin/demo-videos/${id}`, { method: 'PATCH', body: JSON.stringify({ display_name }) });
}

export function deleteDemoVideo(id: string): Promise<{ ok: boolean }> {
  return request(`/v1/admin/demo-videos/${id}`, { method: 'DELETE' });
}

export function rerunDemoVideo(id: string): Promise<{ ok: boolean; message: string }> {
  return request(`/v1/admin/demo-videos/${id}/rerun`, { method: 'POST' });
}

export function getDemoVideoStatus(id: string): Promise<{ id: string; status: string; error_message: string | null; result_count: number }> {
  return request(`/v1/admin/demo-videos/${id}/status`);
}

// ── Vertical Analytics ────────────────────────────────────────────────────────

export interface VerticalCount {
  vertical?: string
  replacing?: string
  label: string
  count: number
}

export interface VerticalAnalytics {
  users_by_vertical: VerticalCount[]
  waitlist_by_vertical: VerticalCount[]
  demo_by_vertical: VerticalCount[]
  replacing_by_option: VerticalCount[]
  total_with_vertical: number
  total_without_vertical: number
}

export function getVerticalAnalytics(range?: string): Promise<VerticalAnalytics> {
  return request<VerticalAnalytics>(`/v1/admin/analytics/verticals${range ? `?range=${range}` : ''}`)
}

export function flushInferenceQueue(): Promise<{ ok: boolean; queue_cleared?: number; active_cancelled?: number; message?: string }> {
  return request('/v1/admin/flush-queue', { method: 'POST' });
}

// ── PMF Analytics ─────────────────────────────────────────────────────────────

export interface PmfAnalytics {
  survey_responses: {
    very_disappointed: number
    somewhat_disappointed: number
    not_disappointed: number
    total: number
    score: number
    note: string
  }
  activation: {
    signups_total: number
    ran_at_least_one: number
    ran_at_least_two: number
    activation_rate: number
  }
  retention: {
    repeat_users_7d: number
    repeat_users_30d: number
  }
  demo_to_signup: {
    demo_signals_total: number
    signups_total: number
  }
  pmf_by_vertical: unknown[]
}

export function getPmfAnalytics(): Promise<PmfAnalytics> {
  return request<PmfAnalytics>('/v1/admin/pmf')
}

export interface UnrecognizedPrompt {
  id: string
  original_term: string
  nearest_match: string | null
  similarity: number | null
  count: number
  created_at: string
}

export function getAdminUnrecognizedPrompts(): Promise<{ entries: UnrecognizedPrompt[] }> {
  return request('/v1/admin/unrecognized-prompts')
}

// ─── PRI-200: full prompt-archive surface ─────────────────────────────────

export interface PromptSubmission {
  id: string
  user_id: string | null
  clerk_user_id: string | null
  anon_ip_hash: string | null
  session_id: string | null
  user_video_id: string | null
  job_id: string | null
  source_kind: 'file_upload' | 'live_stream' | 'recorded_stream'
  is_prompt_update: boolean
  prompt_text: string
  parsed_intent: Record<string, unknown> | null
  inference_model_version: string | null
  source_video_s3_key: string | null
  source_video_duration_s: number | null
  result_metadata: Record<string, unknown> | null
  result_artefacts_s3_prefix: string | null
  status: 'submitted' | 'completed' | 'failed' | 'rejected' | 'cancelled'
  rejection_reason: string | null
  sampling_decision: 'captured' | 'dropped' | 'redacted'
  retention_class: string
  submitted_at: string
  completed_at: string | null
}

export interface PromptSubmissionsList {
  total: number
  limit: number
  offset: number
  entries: PromptSubmission[]
}

export interface PromptSubmissionsFilters {
  limit?: number
  offset?: number
  source_kind?: 'file_upload' | 'live_stream' | 'recorded_stream'
  status?: 'submitted' | 'completed' | 'failed' | 'rejected' | 'cancelled'
  user_id?: string
  q?: string
  since?: string
  until?: string
  include_dropped?: boolean
}

export function getAdminPromptSubmissions(filters: PromptSubmissionsFilters = {}): Promise<PromptSubmissionsList> {
  const params = new URLSearchParams()
  if (filters.limit != null) params.set('limit', String(filters.limit))
  if (filters.offset != null) params.set('offset', String(filters.offset))
  if (filters.source_kind) params.set('source_kind', filters.source_kind)
  if (filters.status) params.set('status', filters.status)
  if (filters.user_id) params.set('user_id', filters.user_id)
  if (filters.q) params.set('q', filters.q)
  if (filters.since) params.set('since', filters.since)
  if (filters.until) params.set('until', filters.until)
  if (filters.include_dropped) params.set('include_dropped', '1')
  const qs = params.toString()
  return request(`/v1/admin/prompt-submissions${qs ? `?${qs}` : ''}`)
}

export function getAdminPromptSubmission(id: string): Promise<PromptSubmission> {
  return request(`/v1/admin/prompt-submissions/${id}`)
}

// ── Design Partner Agreements (admin create/list) ──────────────────────────────

export interface DesignPartnerAgreementAdmin {
  id: string
  token: string
  shareUrl: string
  companyLegalName: string
  useCase: string
  valueOfSolving: string | null
  targetMetrics: string
  scopeOfWork: string | null
  estimatedCostToDeliver: string | null
  priceUsd: number | null
  publicReferenceOptOut: boolean
  status: 'draft' | 'sent' | 'signed' | 'paid' | 'expired' | 'terminated' | 'completed'
  createdBy: string
  createdAt: string
  expiresAt: string
  signedAt: string | null
  paidAt: string | null
}

export interface NewDesignPartnerAgreement {
  companyLegalName: string
  useCase: string
  valueOfSolving?: string | null
  targetMetrics: string
  scopeOfWork?: string | null
  estimatedCostToDeliver?: string | null
  /** Omit to default server-side to the standard $500 token amount. */
  priceUsd?: number | null
  /** Partner declines PUBLIC name/logo/quote use; private investor reference is always retained regardless. Default false. */
  publicReferenceOptOut?: boolean
}

export function createDesignPartnerAgreement(
  body: NewDesignPartnerAgreement,
): Promise<{ id: string; token: string; shareUrl: string; status: string; expiresAt: string }> {
  return request('/v1/admin/design-partner-agreements', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export function getAdminDesignPartnerAgreements(): Promise<{ agreements: DesignPartnerAgreementAdmin[] }> {
  return request('/v1/admin/design-partner-agreements')
}

export type DesignPartnerAgreementAdminStatus = 'sent' | 'paid' | 'terminated' | 'completed'

export function updateDesignPartnerAgreementStatus(
  id: string,
  status: DesignPartnerAgreementAdminStatus,
): Promise<{ id: string; status: string }> {
  return request(`/v1/admin/design-partner-agreements/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  })
}

export function deleteDesignPartnerAgreement(id: string): Promise<{ ok: boolean; id: string }> {
  return request(`/v1/admin/design-partner-agreements/${id}`, { method: 'DELETE' })
}

export function getDesignPartnerAgreementPdfUrl(id: string): Promise<{ url: string }> {
  return request(`/v1/admin/design-partner-agreements/${id}/pdf`)
}
